import { useEffect, useState } from 'react';
import { Check, KeyRound, Loader2, Trash2 } from 'lucide-react';
import { api } from '@/api/client';

/**
 * Third-party credentials.
 *
 * Write-only: a value can be set or cleared, never read back. The API returns
 * only whether each is configured, where it came from, and its last four
 * characters — so this component has nothing to leak even if it wanted to.
 *
 * The Base44 version rendered the Shippo key into a text input from a settings
 * row the browser fetched, which put a key that buys postage in front of
 * anyone who opened devtools.
 */

const FIELDS = [
  {
    name: 'SHIPPO_API_KEY',
    label: 'Shippo API key',
    what: 'Buys and refunds shipping labels.',
    where: 'Shippo dashboard → Settings → API',
    hint: 'Use a test key (shippo_test_…) until you have printed a real label.',
  },
  {
    name: 'ANTHROPIC_API_KEY',
    label: 'Anthropic API key',
    what: 'Reads the route table off a scanned PS Form 3602.',
    where: 'console.anthropic.com → API keys',
    hint: 'Without it, routes are entered by hand.',
  },
  {
    name: 'GOOGLE_PLACES_API_KEY',
    label: 'Google Places API key',
    what: 'Finds destination post office addresses by ZIP.',
    where: 'Google Cloud Console → APIs & Services → Credentials',
    hint: 'Enable the Places API (New) and restrict the key to it.',
  },
  {
    name: 'USPS_CONSUMER_KEY',
    label: 'USPS Consumer Key',
    what:
      'Looks up which post office an EDDM mailing for a ZIP is actually dropped at. ' +
      'That is often not the office in that ZIP — 46543 (Millersburg) is served by ' +
      'Syracuse — and USPS is the only source that knows.',
    where: 'cop.usps.com → Developer Apps → Add App → Manage',
    hint: 'Free with a Business Customer Gateway account. Needs the Secret below as well.',
  },
  {
    name: 'USPS_CONSUMER_SECRET',
    label: 'USPS Consumer Secret',
    what: 'The other half of the USPS credential. Also standardises addresses into USPS form.',
    where: 'Issued alongside the Consumer Key on the same screen',
    hint: 'Both values are needed; one on its own does nothing.',
  },
  {
    name: 'MAILGUN_API_KEY',
    label: 'Mailgun API key',
    what: 'Sends invitations and password reset codes from the shop’s own domain.',
    where: 'Mailgun → Send → Sending keys (or Settings → API keys)',
    hint: 'Set the domain, region and sending address under Email above as well.',
  },
];

export default function SecretsSection() {
  const [status, setStatus] = useState({});
  const [available, setAvailable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(null);
  const [saved, setSaved] = useState(null);
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const result = await api.settings.secrets();
      setStatus(Object.fromEntries(result.secrets.map((s) => [s.name, s])));
      setAvailable(result.storage_available);
    } catch (err) {
      setError(
        err.isForbidden ? 'Only an administrator can manage credentials.' : err.message
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (name) => {
    const value = (drafts[name] ?? '').trim();
    if (!value) return;

    setBusy(name);
    setError('');
    try {
      const next = await api.settings.setSecret(name, value);
      setStatus((s) => ({ ...s, [name]: next }));
      // Clear the field immediately: there is no reason for the value to sit
      // in the DOM once it has been sent.
      setDrafts((d) => ({ ...d, [name]: '' }));
      setSaved(name);
      setTimeout(() => setSaved(null), 2500);
    } catch (err) {
      setError(err.message || `Could not save ${name}.`);
    } finally {
      setBusy(null);
    }
  };

  const clear = async (name) => {
    setBusy(name);
    setError('');
    try {
      const next = await api.settings.clearSecret(name);
      setStatus((s) => ({ ...s, [name]: next }));
    } catch (err) {
      setError(err.message || `Could not remove ${name}.`);
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="page-spinner" style={{ minHeight: '12rem' }}>
        <Loader2 size={24} className="spin" />
      </div>
    );
  }

  return (
    <div className="card">
      <div className="form-title">
        <KeyRound size={18} style={{ display: 'inline', marginRight: '0.5rem' }} />
        Integration credentials
      </div>

      <p className="muted" style={{ marginBottom: '1.25rem' }}>
        Keys are stored encrypted and used by the server. They cannot be read back —
        only replaced or removed.
      </p>

      {error && <div className="alert alert-error">{error}</div>}

      {!available && (
        <div className="alert alert-warning">
          Saving is disabled because <code>SECRETS_ENCRYPTION_KEY</code> is not set on the
          server. Keys already set as environment variables still work; they just cannot
          be changed from here.
        </div>
      )}

      {FIELDS.map((field) => {
        const current = status[field.name] ?? { configured: false };
        const isBusy = busy === field.name;

        return (
          <div key={field.name} className="secret-row">
            <div className="secret-head">
              <label className="form-label" htmlFor={field.name}>
                {field.label}
              </label>

              {current.configured ? (
                <span className={`badge ${current.source === 'database' ? 'badge-green' : 'badge-amber'}`}>
                  {current.source === 'database'
                    ? `set ····${current.last_four}`
                    : `from server ····${current.last_four}`}
                </span>
              ) : (
                <span className="badge badge-red">not set</span>
              )}
            </div>

            <p className="secret-what">{field.what}</p>

            <div className="secret-controls">
              <input
                id={field.name}
                className="form-input"
                type="password"
                autoComplete="off"
                spellCheck="false"
                placeholder={current.configured ? 'Enter a new value to replace' : 'Paste the key'}
                value={drafts[field.name] ?? ''}
                onChange={(e) => setDrafts((d) => ({ ...d, [field.name]: e.target.value }))}
                disabled={!available || isBusy}
              />

              <button
                className="btn btn-primary"
                onClick={() => save(field.name)}
                disabled={!available || isBusy || !(drafts[field.name] ?? '').trim()}
              >
                {isBusy ? <Loader2 size={16} className="spin" /> : null}
                {saved === field.name ? <Check size={16} /> : null}
                {saved === field.name ? 'Saved' : 'Save'}
              </button>

              {current.configured && current.source === 'database' && (
                <button
                  className="btn btn-outline"
                  onClick={() => clear(field.name)}
                  disabled={isBusy}
                  title="Remove the stored key; any server environment value takes over"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>

            <p className="secret-hint">
              {field.hint} <span className="secret-where">Get one: {field.where}</span>
            </p>
          </div>
        );
      })}
    </div>
  );
}
